import clsx from 'clsx';
import React__default, { lazy, useState, useRef, useEffect, Suspense, useMemo } from 'react';
import { Checkbox } from '../checkboxes/index.js';
import { cssDropdownWrapper, cssDropdownButton, cssDropdownOptionsContainer, cssDropdownOption, cssDropdownOptionFocused } from './index.css.js';

/* eslint-disable @typescript-eslint/ban-ts-comment */
const Clear = lazy(() => import('./Clear.js'));
let clearTypeAhead;
function useOptionsFromChildrenAndProps(children, options) {
    return useMemo(() => {
        if (options) {
            return options;
        }
        else if (!options && children && Array.isArray(children)) {
            return children.map((o) => ({
                label: o.props.children,
                value: o.props.value,
                disabled: !!o.props.disabled
            }));
        }
        else if (!options && children && !Array.isArray(children)) {
            return [
                {
                    label: children.props.children,
                    value: children.props.value,
                    disabled: !!children.props.disabled
                }
            ];
        }
        else {
            return [];
        }
    }, [children, options]);
}
const getSelectedValue = (selectedOptions, Options, placeholder) => {
    const isArray = Array.isArray(selectedOptions);
    const isObj = typeof selectedOptions === 'object';
    const isNumberArray = isArray && typeof selectedOptions[0] === 'number';
    if (placeholder && !selectedOptions) {
        return [-1];
    }
    else if (!selectedOptions) {
        return [0];
    }
    else if (!isArray && !isObj) {
        return [selectedOptions];
    }
    else if (isNumberArray) {
        return selectedOptions;
    }
    else if (isArray && !isNumberArray) {
        return Options.map((e) => selectedOptions.findIndex((s) => s.value === e.value) || -1).filter((e) => e === -1);
    }
    else if (!isArray && isObj) {
        return [Options.findIndex((e) => e.value === selectedOptions.value)];
    }
    return [];
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useSelectedOptionIndexes(selectedOptions, placeholder, Options, multiple) {
    const [selected, setSelected] = useState(getSelectedValue(selectedOptions, Options, placeholder));
    useEffect(() => {
        setSelected(getSelectedValue(selectedOptions, Options, placeholder));
    }, [selectedOptions, Options, placeholder]);
    return {
        selected,
        addSelected: (i) => multiple ? setSelected([...selected.filter((s) => s !== -1), i]) : setSelected([i]),
        removeSelected: (i) => multiple ? setSelected(selected.filter((s) => s !== i)) : setSelected([placeholder ? -1 : 0]),
        clearSelected: () => setSelected([placeholder ? -1 : 0]),
        getPreview: () => {
            if (!selected || selected.length === 0 || selected[0] === -1) {
                return React__default.createElement("span", null, placeholder);
            }
            if (multiple) {
                return (React__default.createElement(React__default.Fragment, null, selected.map((option, idx) => {
                    const label = Options[option]?.label;
                    const separator = idx < selected.length - 1 ? React__default.createElement("span", null, ", ") : React__default.createElement("span", null);
                    return (React__default.createElement("span", { key: idx },
                        typeof label === 'string' ? React__default.createElement("span", null, label) : label,
                        separator));
                })));
            }
            const label = Options[selected[0]]?.label;
            return typeof label === 'string' ? React__default.createElement("span", null, label) : label;
        }
    };
}
let numDropdowns = 0;
const Dropdown = ({ placeholder = 'Select', multiple, children, options, popoverStyle, optionStyle, placeholderStyle, label, hideLabel = false, selectedOptions, onSelect, onPreview, clearAll, 
// required = false,
className, disabled, hideSelectTag = false, style }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [id] = useState(() => numDropdowns++);
    const Options = useOptionsFromChildrenAndProps(children, options);
    const { selected, addSelected, removeSelected, getPreview, clearSelected } = useSelectedOptionIndexes(selectedOptions, placeholder, Options, multiple);
    const [focused, setFocused] = useState(0);
    const containerRef = useRef(null);
    const wrapperRef = useRef(null);
    const offContainerClick = (e) => {
        if (!!e && !!e.target && !!wrapperRef && !!wrapperRef.current) {
            const targetNode = e.target;
            if (!!targetNode && !wrapperRef.current.contains(targetNode)) {
                setMenuOpen(false);
            }
        }
    };
    useEffect(() => {
        window.addEventListener('click', offContainerClick, false);
        return () => {
            window.removeEventListener('click', offContainerClick, false);
        };
    }, []);
    const onSelectOption = (o, i) => (e) => {
        e.preventDefault();
        if (o.disabled) {
            return;
        }
        setMenuOpen(false);
        const isCurrentIncluded = selected.includes(i);
        if (isCurrentIncluded) {
            if (multiple) {
                removeSelected(i);
            }
        }
        else {
            addSelected(i);
        }
        if (onSelect) {
            const allSelected = [];
            selected.forEach((idx) => {
                if (idx > -1) {
                    if (!(isCurrentIncluded && i === idx)) {
                        allSelected.push(Options[idx]);
                    }
                }
            });
            if (!isCurrentIncluded) {
                allSelected.push(Options[i]);
            }
            onSelect(o, i, allSelected);
        }
    };
    useEffect(() => {
        if (containerRef.current) {
            if (menuOpen) {
                containerRef.current.focus();
            }
            else if (selected.length === 0 || selected.includes(-1)) {
                containerRef.current.blur();
            }
        }
    }, [menuOpen, selected]);
    const [typeAhead, setTypeAhead] = useState('');
    const KeyEventHandler = (e) => {
        switch (e.key) {
            case 'Enter':
                if (!menuOpen) {
                    setMenuOpen(true);
                }
                else {
                    setMenuOpen(false);
                    if (focused !== -1)
                        selected.includes(focused) ? removeSelected(focused) : addSelected(focused);
                }
                break;
            case 'Escape':
                if (menuOpen) {
                    setMenuOpen(false);
                }
                break;
            case 'ArrowDown':
                if (!menuOpen)
                    setMenuOpen(true);
                setFocused((prev) => {
                    if (prev === -1) {
                        return 0;
                    }
                    else if (prev + 1 >= Options.length) {
                        return 0;
                    }
                    else {
                        return prev + 1;
                    }
                });
                break;
            case 'ArrowUp':
                setFocused((prev) => {
                    if (prev === -1) {
                        return Options.length - 1;
                    }
                    else if (prev - 1 < 0) {
                        return Options.length - 1;
                    }
                    else {
                        return prev - 1;
                    }
                });
                break;
            default:
                if (!multiple) {
                    let found = false;
                    const newTypeAhead = typeAhead + e.key;
                    setTypeAhead(newTypeAhead);
                    const pattern = new RegExp(`^${newTypeAhead}`, 'ig');
                    Options.forEach((option, idx) => {
                        if (!found && pattern.test(String(option.label))) {
                            setFocused(idx);
                            addSelected(idx);
                            setMenuOpen(false);
                            onSelect && onSelect(option, idx, []);
                            clearTimeout(clearTypeAhead);
                            found = true;
                        }
                    });
                    if (found) {
                        clearTypeAhead = setTimeout(() => {
                            setTypeAhead('');
                        }, 3000);
                    }
                    else {
                        setTypeAhead('');
                        setMenuOpen(false);
                    }
                }
                break;
        }
    };
    const handleClear = () => {
        setMenuOpen(false);
        clearSelected();
        if (onSelect) {
            onSelect({}, -1, []);
        }
    };
    const handleOnClick = () => {
        if (disabled) {
            setMenuOpen(false);
            return;
        }
        if (!menuOpen && multiple)
            setFocused(-1);
        if (!menuOpen && !multiple)
            setFocused(selected[0]);
        setMenuOpen(!menuOpen);
    };
    const selectedOptionNodes = typeof onPreview === 'function' ? onPreview(selected) : getPreview();
    const labelId = `dropdown-label-${id}`;
    return (React__default.createElement("div", { ref: wrapperRef, className: clsx('c-dropdown', 'c-dropdown--wrap', cssDropdownWrapper, className, {
            'c-dropdown--multiple': multiple
        }), style: style },
        !!label && !hideLabel && (React__default.createElement("label", { id: labelId, className: 'c-input-label' }, label)),
        hideSelectTag ? null : (React__default.createElement("select", { name: 'option', value: Options[selected[0]]?.value }, Options.map((o, i) => (React__default.createElement("option", { key: i, value: o?.value }, String(o?.label)))))),
        React__default.createElement("button", { className: clsx('c-dropdown__container', cssDropdownButton, {
                'c-dropdown__container--has-value': selected.length !== 0 && !selected.includes(-1)
            }), disabled: disabled, "aria-expanded": menuOpen ? 'true' : 'false', "aria-label": hideLabel && typeof label === 'string' ? label : undefined, "aria-labelledby": hideLabel ? undefined : labelId, onClick: handleOnClick, style: selected[0] === -1 ? placeholderStyle : optionStyle, tabIndex: 0, onKeyDown: KeyEventHandler }, selectedOptionNodes),
        React__default.createElement("div", { style: popoverStyle, className: clsx('c-dropdown__options-container', cssDropdownOptionsContainer, {
                'c-dropdown--active': menuOpen
            }) },
            clearAll && (React__default.createElement(Suspense, { fallback: '' },
                React__default.createElement(Clear, { onClick: handleClear }))),
            Options.map((o, i) => (React__default.createElement("div", { style: optionStyle, onClick: onSelectOption(o, i), key: i, "aria-selected": selected.includes(i), tabIndex: i + 1, className: clsx('c-dropdown__option', cssDropdownOption, {
                    'c-dropdown__option--disabled': o.disabled,
                    [cssDropdownOptionFocused]: focused === i
                }) },
                multiple && (React__default.createElement(Checkbox, { onClick: onSelectOption(o, i), checked: selected.includes(i), disabled: o.disabled })),
                React__default.createElement("span", null, o?.label)))))));
};

export { Dropdown };
//# sourceMappingURL=index.js.map
